import { StatusIndicator } from '@diligentcorp/atlas-theme-mui-lens';
import React from 'react';
import { overdueMitigationColorResolver } from 'src/components/atlas/organisms/helpers/sharedStatusColorResolvers';
import { parseUploadedFilesFromAnswerValue } from 'src/utils/dynamicQuestionUploadedFiles';
import {
  formatDynamicQuestionAnswerDisplay,
  type DynamicQuestionOption,
} from 'src/utils/formatDynamicQuestionAnswerDisplay';
import {
  DynamicQuestionDescription,
  DynamicQuestionLabel,
  ReadOnlyCard,
  ReadOnlyValue,
} from './DynamicQuestion.styles';
import { DynamicQuestionFileDownloadList } from './DynamicQuestionFileDownloadList';

export interface DynamicQuestionAnswerReadOnlyFallbackProps {
  questionText: string;
  description?: string | null;
  answerValue: unknown;
  formatOptions?: DynamicQuestionOption[] | null;
  reportId: string;
  fileDownloadCaseManagerView?: boolean;
  'data-testid'?: string;
  isFlagged?: boolean | null;
}

export const DynamicQuestionAnswerReadOnlyFallback: React.FC<
  DynamicQuestionAnswerReadOnlyFallbackProps
> = ({
  questionText,
  description,
  answerValue,
  formatOptions,
  reportId,
  fileDownloadCaseManagerView = false,
  'data-testid': dataTestId,
  isFlagged = false,
}) => {
  const answerValueObj =
    answerValue != null &&
    typeof answerValue === 'object' &&
    !Array.isArray(answerValue)
      ? (answerValue as Record<string, unknown>)
      : null;

  if (answerValueObj?.isSanitized === true) {
    return (
      <ReadOnlyCard data-testid={dataTestId}>
        <DynamicQuestionLabel>
          <div>{questionText}</div>
          {isFlagged && (
            <StatusIndicator
              customColor={overdueMitigationColorResolver}
              label={'Flagged answer'}
              aria-label={'Flagged answer'}
            />
          )}
        </DynamicQuestionLabel>
        {description != null && description !== '' && (
          <DynamicQuestionDescription>{description}</DynamicQuestionDescription>
        )}
        <ReadOnlyValue
          data-testid={
            dataTestId != null ? `${dataTestId}-fallback-value` : undefined
          }
        >
          [SANITIZED]
        </ReadOnlyValue>
      </ReadOnlyCard>
    );
  }

  const uploaded = parseUploadedFilesFromAnswerValue(answerValue);
  const showFileDownloads =
    reportId !== '' &&
    uploaded.length > 0 &&
    uploaded.every((f) => f.s3Path.trim() !== '');
  return (
    <ReadOnlyCard data-testid={dataTestId}>
      <DynamicQuestionLabel>
        <div>{questionText}</div>
        {isFlagged && (
          <StatusIndicator
            customColor={overdueMitigationColorResolver}
            label={'Flagged answer'}
            aria-label={'Flagged answer'}
          />
        )}
      </DynamicQuestionLabel>
      {description != null && description !== '' && (
        <DynamicQuestionDescription>{description}</DynamicQuestionDescription>
      )}
      {showFileDownloads ? (
        <DynamicQuestionFileDownloadList
          files={uploaded}
          reportId={reportId}
          caseManagerView={fileDownloadCaseManagerView}
        />
      ) : (
        <ReadOnlyValue
          data-testid={
            dataTestId != null ? `${dataTestId}-fallback-value` : undefined
          }
        >
          {formatDynamicQuestionAnswerDisplay(answerValue, formatOptions)}
        </ReadOnlyValue>
      )}
    </ReadOnlyCard>
  );
};
